import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { ExternalLink, FileText, Loader2, Paperclip } from 'lucide-react';
import { useDocsDocument } from '@/api/docs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { fmtBytes } from '@/lib/rmm-helpers';

/**
 * In-app IT Glue document viewer. Sections arrive as HTML from the server (embedded password widgets
 * already replaced with a placeholder) and are sanitised here with DOMPurify. Rendered on white like
 * the .eml viewer, because IT Glue content carries its own light-theme inline styles. Images are
 * pre-signed S3 URLs that expire after an hour; the query's staleTime keeps them inside that.
 */
const SANITIZE: Parameters<typeof DOMPurify.sanitize>[1] = { ADD_ATTR: ['target'], FORBID_TAGS: ['style', 'form', 'input', 'button'] };

export function DocumentViewer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data, isLoading, error } = useDocsDocument(id);
  const html = useMemo(() => {
    if (!data?.html) return '';
    const clean = DOMPurify.sanitize(data.html, SANITIZE) as unknown as string;
    // Links open outside the SPA.
    return clean.replace(/<a /g, '<a target="_blank" rel="noreferrer" ');
  }, [data?.html]);

  return (
    <Dialog open={!!id} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="flex max-h-[88vh] w-[min(960px,94vw)] max-w-none flex-col gap-0 p-0">
        <DialogHeader className="border-b border-divider px-5 py-3">
          <DialogTitle className="flex items-center gap-2 pr-6 text-base">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{data?.name ?? 'Document'}</span>
          </DialogTitle>
          {data && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {data.updatedAt && <span>Updated {fmtRelative(data.updatedAt)}</span>}
              {data.url && <a href={data.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground hover:underline">Open in IT Glue <ExternalLink className="h-3 w-3" /></a>}
            </div>
          )}
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading document…</div>
          ) : error ? (
            <div className="text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load'}</div>
          ) : data ? (
            <>
              {data.attachments.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {data.attachments.map((a) => (
                    <a key={a.name + a.url} href={a.url ?? undefined} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-foreground hover:border-ring">
                      <Paperclip className="h-3 w-3" /> {a.name}{a.size != null && <span className="text-muted-foreground">· {fmtBytes(a.size)}</span>}
                    </a>
                  ))}
                </div>
              )}
              {html ? (
                <div
                  className="itg-doc rounded-md bg-white p-5 text-sm leading-relaxed text-neutral-900 [&_a]:text-blue-700 [&_a]:underline [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:mb-1.5 [&_h3]:mt-3 [&_h3]:font-semibold [&_h4]:mt-2 [&_h4]:font-semibold [&_img]:max-w-full [&_li]:ml-5 [&_ol]:list-decimal [&_p]:my-1.5 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-neutral-100 [&_pre]:p-2 [&_table]:my-2 [&_table]:max-w-full [&_td]:border [&_td]:border-neutral-300 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-neutral-300 [&_th]:px-2 [&_th]:py-1 [&_ul]:list-disc [&_.itg-gallery]:my-2 [&_.itg-gallery]:grid [&_.itg-gallery]:grid-cols-2 [&_.itg-gallery]:gap-2 [&_.itg-gallery_img]:rounded [&_.itg-gallery_img]:border [&_.itg-gallery_img]:border-neutral-200"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ) : (
                <div className="py-6 text-sm text-muted-foreground">{data.uploaded ? 'This is an uploaded file — open the attachment above.' : 'This document has no content.'}</div>
              )}
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
